import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { SessionVaultService } from '../../../core/session-vault.service';

@Component({
  selector: 'app-encryption-password-dialog',
  imports: [ReactiveFormsModule],
  template: `
    @if (sessionVaultService.encryptionPromptState(); as promptState) {
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
        <div
          class="w-full max-w-lg rounded-3xl border border-white/10 bg-slate-950 p-6 shadow-2xl shadow-slate-950/40"
          aria-modal="true"
          role="dialog"
          aria-labelledby="encryption-password-dialog-title"
        >
          <p class="text-xs uppercase tracking-[0.3em] text-white/45">Session Encryption</p>
          <h2 id="encryption-password-dialog-title" class="mt-3 text-2xl font-semibold text-white">
            Encryption password required
          </h2>
          <p class="mt-3 text-sm leading-6 text-white/70">{{ promptState.reason }}</p>

          <form class="mt-6 space-y-4" [formGroup]="form" (ngSubmit)="submit()">
            <div>
              <label class="mb-2 block text-sm font-medium text-white/80" for="session-encryption-password">
                Encrypt password
              </label>
              <input
                id="session-encryption-password"
                formControlName="password"
                class="w-full rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-white outline-none transition focus:border-cyan-300"
                type="password"
                autocomplete="current-password"
              />
            </div>

            @if (localErrorMessage() || promptState.errorMessage) {
              <div class="rounded-2xl border border-rose-400/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-100" role="alert" aria-live="assertive">
                {{ localErrorMessage() || promptState.errorMessage }}
              </div>
            }

            <div class="flex flex-wrap gap-3">
              <button
                type="submit"
                class="rounded-2xl bg-cyan-300 px-4 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-200 disabled:cursor-not-allowed disabled:bg-cyan-300/40"
                [disabled]="form.invalid"
              >
                Continue
              </button>
              <button
                type="button"
                class="rounded-2xl border border-white/15 px-4 py-3 text-sm font-medium text-white/80 transition hover:border-white/30 hover:text-white"
                (click)="cancel()"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EncryptionPasswordDialogComponent {
  protected readonly sessionVaultService = inject(SessionVaultService);

  private readonly formBuilder = inject(NonNullableFormBuilder);
  protected readonly localErrorMessage = signal<string | null>(null);
  protected readonly form = this.formBuilder.group({
    password: ['', [Validators.required]],
  });

  protected submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.localErrorMessage.set('Encryption password is required.');
      return;
    }

    this.localErrorMessage.set(null);
    this.sessionVaultService.submitEncryptionPassword(this.form.controls.password.getRawValue());
    this.form.reset({ password: '' });
  }

  protected cancel(): void {
    this.localErrorMessage.set(null);
    this.form.reset({ password: '' });
    this.sessionVaultService.cancelEncryptionPasswordRequest();
  }
}